/**
 * ROI calculator — single source of truth.
 *
 * Numbers come from `docs/roi-calculator-spec.md` (practitioner interviews
 * across large firms, the mid-tier, and small firms). Investor-facing
 * page only. Reachable publicly via `/roi` and inside the demo sidebar via
 * `/roi-calculator`.
 *
 * Edit the constants in this file to update the page; totals are derived,
 * so changes flow through without touching the UI.
 */

export interface ChargeOutRate {
  role: string;
  hoursPerCase: string;
  rate: string;
}

export interface RolePreset {
  role: string;
  chargeOutRate: number;
  hoursPerCase: number;
}

export interface TierRange {
  /** Low end of the range, used for total calculations and display. */
  low: number;
  /** High end of the range (optional — single-value tiers omit it). */
  high?: number;
}

export interface Tier {
  id: 'large' | 'mid' | 'small';
  label: string;
  profile: string;
  casesPerYear: TierRange;
  spendPerCase: number;
  /**
   * Editable role presets for the self-service calculator. Each preset sums
   * to exactly the tier's spendPerCase baseline, so the default blended WIP
   * per case reproduces the validated figure.
   */
  rolePresets: RolePreset[];
  timeSavingPercent: number;
  goingToPlanPercent: TierRange;
  chargeOutRates: ChargeOutRate[];
  notes: string[];
}

export interface ContentiousModule {
  label: string;
  profile: string;
  annualSpend: number;
  writeOffPercent: number;
  reductionPercent: number;
  successRate: string;
  wipAtRiskPerCase: string;
  capabilities: string[];
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Tier data                                                                */
/* ──────────────────────────────────────────────────────────────────────── */

export const LARGE: Tier = {
  id: 'large',
  label: 'Large firms',
  profile: 'Big Four & Top 10 firms, 30+ IPs per practice',
  casesPerYear: { low: 100 },
  spendPerCase: 93_250,
  rolePresets: [
    { role: 'Partner', chargeOutRate: 900, hoursPerCase: 25 },
    { role: 'Manager', chargeOutRate: 600, hoursPerCase: 75 },
    { role: 'Associate', chargeOutRate: 250, hoursPerCase: 95 },
    { role: 'Support staff', chargeOutRate: 100, hoursPerCase: 20 },
  ],
  timeSavingPercent: 60,
  goingToPlanPercent: { low: 20, high: 30 },
  chargeOutRates: [
    { role: 'IP Partner', hoursPerCase: '25 hrs', rate: '£900/hr' },
    { role: 'Manager', hoursPerCase: '75 hrs', rate: '£600/hr' },
    { role: 'Associate', hoursPerCase: '95 hrs', rate: '£250/hr' },
    { role: 'Support staff', hoursPerCase: '20 hrs', rate: '£100/hr' },
  ],
  notes: [
    'Fixed fees for CVLs, then overspill charged hourly',
    'Onboarding alone can cost £25–30k per case',
    'Cases rarely go to plan because of scale',
  ],
};

export const MID: Tier = {
  id: 'mid',
  label: 'Mid-tier firms',
  profile: 'Firms with 2–20 IPs, roughly 30–40 employees',
  casesPerYear: { low: 200 },
  spendPerCase: 23_250,
  rolePresets: [
    { role: 'Partner', chargeOutRate: 450, hoursPerCase: 15 },
    { role: 'Manager', chargeOutRate: 225, hoursPerCase: 45 },
    { role: 'Associate', chargeOutRate: 90, hoursPerCase: 60 },
    { role: 'Support staff', chargeOutRate: 65, hoursPerCase: 15 },
  ],
  timeSavingPercent: 60,
  goingToPlanPercent: { low: 50, high: 60 },
  chargeOutRates: [
    { role: 'IP Partner', hoursPerCase: '15 hrs', rate: '£450/hr' },
    { role: 'Manager', hoursPerCase: '45 hrs', rate: '£225/hr' },
    { role: 'Associate', hoursPerCase: '60 hrs', rate: '£90/hr' },
    { role: 'Support staff', hoursPerCase: '15 hrs', rate: '£65/hr' },
  ],
  notes: [
    'Higher savings (up to 70%) possible, since work is more standardised',
    '£450/hr IP rate remains below a larger firm’s £900/hr rate',
  ],
};

export const SMALL: Tier = {
  id: 'small',
  label: 'Small firms to sole practitioners',
  profile: 'Small firm or sole IP with a few junior members of staff',
  casesPerYear: { low: 100 },
  spendPerCase: 11_950,
  rolePresets: [
    { role: 'Partner', chargeOutRate: 250, hoursPerCase: 10 },
    { role: 'Manager', chargeOutRate: 150, hoursPerCase: 25 },
    { role: 'Associate', chargeOutRate: 90, hoursPerCase: 50 },
    { role: 'Support staff', chargeOutRate: 60, hoursPerCase: 20 },
  ],
  timeSavingPercent: 60,
  goingToPlanPercent: { low: 70, high: 80 },
  chargeOutRates: [],
  notes: [
    'Baseline volume is 100 cases per year',
    'Mostly standard CVLs with known creditor lists, so 70–80% go to plan',
  ],
};

export const TIERS: readonly Tier[] = [LARGE, MID, SMALL];

/* ──────────────────────────────────────────────────────────────────────── */
/*  Contentious module                                                       */
/* ──────────────────────────────────────────────────────────────────────── */

export const CONTENTIOUS: ContentiousModule = {
  label: 'Contentious insolvency',
  profile: 'GT-scale contingent-fee asset-recovery work',
  annualSpend: 5_000_000,
  writeOffPercent: 50,
  reductionPercent: 60,
  successRate: '50/50',
  wipAtRiskPerCase: '£250k+',
  capabilities: [
    'Risk analysis at case inception: risk versus reward',
    'Historical time-cost predictions from comparable cases',
    'Mapping by location, type, and asset profile',
  ],
};

/* ──────────────────────────────────────────────────────────────────────── */
/*  Derived totals                                                           */
/* ──────────────────────────────────────────────────────────────────────── */

function midpoint(range: TierRange): number {
  return range.high === undefined ? range.low : (range.low + range.high) / 2;
}

export interface TierTotals {
  /** Annual WIP at the low end of the case-count range. */
  wipLow: number;
  /** Annual WIP at the high end of the case-count range. */
  wipHigh: number;
  /** Annual saving at the low end. */
  savingLow: number;
  /** Annual saving at the high end. */
  savingHigh: number;
  /** Saving midpoint — useful for the single "headline" number. */
  savingMid: number;
}

export function tierTotals(tier: Tier): TierTotals {
  const { low, high } = tier.casesPerYear;
  const wipLow = low * tier.spendPerCase;
  const wipHigh = (high ?? low) * tier.spendPerCase;
  const savingFactor = tier.timeSavingPercent / 100;
  const savingLow = wipLow * savingFactor;
  const savingHigh = wipHigh * savingFactor;
  const savingMid = midpoint({ low: savingLow, high: savingHigh });
  return { wipLow, wipHigh, savingLow, savingHigh, savingMid };
}

export interface ContentiousTotals {
  writeOff: number;
  saving: number;
  residualWriteOff: number;
}

export function contentiousTotals(m: ContentiousModule): ContentiousTotals {
  const writeOff = m.annualSpend * (m.writeOffPercent / 100);
  const saving = writeOff * (m.reductionPercent / 100);
  return { writeOff, saving, residualWriteOff: writeOff - saving };
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Formatting helpers                                                       */
/* ──────────────────────────────────────────────────────────────────────── */

/**
 * Render an amount in pounds as a compact human-readable string.
 * `1_500_000` → `"£1.5M"`, `756_000` → `"£756k"`.
 */
export function formatGBPCompact(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    const fixed = millions >= 10 ? millions.toFixed(0) : millions.toFixed(1);
    return `£${fixed.replace(/\.0$/, '')}M`;
  }
  if (amount >= 1_000) {
    const thousands = Math.round(amount / 1_000);
    return `£${thousands}k`;
  }
  return `£${amount.toLocaleString('en-GB')}`;
}

export function formatGBPRange(low: number, high: number): string {
  if (low === high) return formatGBPCompact(low);
  const lo = formatGBPCompact(low);
  const hi = formatGBPCompact(high);
  // When both endpoints share the same scale suffix (M or k), drop the
  // suffix on the lower bound for a tighter financial-comms format —
  // `£2.4M–£3.6M` becomes `£2.4–3.6M`. Keeps the headline on one line in
  // the tier-card slot.
  const matcher = /^£([\d.]+)([Mk])$/;
  const loMatch = lo.match(matcher);
  const hiMatch = hi.match(matcher);
  if (loMatch && hiMatch && loMatch[2] === hiMatch[2]) {
    return `£${loMatch[1]}–${hiMatch[1]}${hiMatch[2]}`;
  }
  return `${lo}–${hi}`;
}

export function formatRange(range: TierRange, suffix = ''): string {
  if (range.high === undefined) return `${range.low}${suffix}`;
  return `${range.low}–${range.high}${suffix}`;
}
